import React, { useState, useEffect } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, UserPlus, ArrowLeft, RefreshCw, Upload, FileText } from 'lucide-react';

import Header from '../../components/ui/Header';
import { PartnerApplication, WorkflowStage } from './types';
import ApplicationQueueList from './components/ApplicationQueueList';
import ApplicationReviewPanel from './components/ApplicationReviewPanel';
import WorkflowProgressTracker from './components/WorkflowProgressTracker';
import partnerApplicationService from '../../services/partnerApplicationService';
import { useAuth } from '../../contexts/AuthContext';

const PartnerOnboardingWorkflow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<PartnerApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [workflowStages, setWorkflowStages] = useState<WorkflowStage[]>([
    { id: '2', name: 'Pre-Approval Review', status: 'pending' },
    { id: '5', name: 'Account Creation', status: 'pending' }
  ]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [contractConditionsNotes, setContractConditionsNotes] = useState('');
  const [onboardingCallNotes, setOnboardingCallNotes] = useState('');
  const [isCompletingCall, setIsCompletingCall] = useState(false);
  const [showAccountCreationModal, setShowAccountCreationModal] = useState<boolean>(false);
  const [generatedPassword, setGeneratedPassword] = useState<string>('');
  const [accountEmail, setAccountEmail] = useState<string>('');
  const [accountFullName, setAccountFullName] = useState<string>('');
  const [accountPhone, setAccountPhone] = useState<string>('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState<boolean>(false);
  const [commissionRate, setCommissionRate] = useState<number>(10);
  const [monthlyPrice, setMonthlyPrice] = useState<string>('');
  const [contractTimeframe, setContractTimeframe] = useState<3 | 6 | 9 | 12>(12);
  const [paymentTerms, setPaymentTerms] = useState<'upfront' | 'monthly'>('monthly');
  const [contractPdfFile, setContractPdfFile] = useState<File | null>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Service now ALWAYS returns array (never null/undefined)
      const partners = await partnerApplicationService.getPendingApplications();
      
      // Safe to map - partners is guaranteed to be an array
      const transformedApps = partners.map(partner => ({
        partner: partner,
        documents: [],
        offers_count: 0
      }));
      
      setApplications(transformedApps);
      
      // CRITICAL FIX: Auto-select on data load if no selection exists
      // This prevents "only one shown" issue by ensuring selection after refresh
      if (!selectedApplication && transformedApps.length > 0) {
        const oldestUnchanged = findOldestUnchangedApplication();
        if (oldestUnchanged) {
          handleSelectApplication(oldestUnchanged);
        }
      }
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setError(err.message || 'Failed to load applications');
      // Set empty array on error
      setApplications([]);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  // Enhanced useEffect to handle auto-selection of oldest unchanged application
  useEffect(() => {
    const selectedPartnerId = location.state?.selectedPartnerId;
    if (selectedPartnerId && applications && applications.length > 0) {
      const preSelectedApp = applications.find(app => app?.partner?.id === selectedPartnerId);
      if (preSelectedApp) {
        handleSelectApplication(preSelectedApp);
      }
    } else if (!selectedApplication && applications && applications.length > 0) {
      // When no application is selected, default to the oldest unchanged application
      const oldestUnchangedApp = findOldestUnchangedApplication();
      if (oldestUnchangedApp) {
        handleSelectApplication(oldestUnchangedApp);
      }
    }
  }, [location.state, applications]);

  // NEW: Helper function to find oldest unchanged application based on current workflow stage
  const findOldestUnchangedApplication = (): PartnerApplication | null => {
    if (!applications || applications.length === 0) return null;

    // Priority order: pending → pre_approved without onboarding → pre_approved with onboarding but no contract
    // → pre_approved with contract but not signed → pre_approved signed but no account → pre_approved with account
    
    // 1. Check for pending applications (oldest workflow stage)
    const pendingApps = applications.filter(app => app.partner.status === 'pending');
    if (pendingApps.length > 0) return pendingApps[0];

    // 2. Check for pre-approved without onboarding call
    const needsOnboardingApps = applications.filter(
      app => app.partner.status === 'pre_approved' && !app.partner.onboarding_call_completed
    );
    if (needsOnboardingApps.length > 0) return needsOnboardingApps[0];

    // 3. Check for pre-approved with onboarding but no contract conditions
    const needsContractConditionsApps = applications.filter(
      app => app.partner.status === 'pre_approved' && 
      app.partner.onboarding_call_completed && 
      !app.partner.contract_conditions_added
    );
    if (needsContractConditionsApps.length > 0) return needsContractConditionsApps[0];

    // 4. Check for pre-approved with conditions but contract not sent
    const needsContractSentApps = applications.filter(
      app => app.partner.status === 'pre_approved' && 
      app.partner.contract_conditions_added && 
      !app.partner.contract_sent_at
    );
    if (needsContractSentApps.length > 0) return needsContractSentApps[0];

    // 5. Check for pre-approved with contract sent but not signed
    const needsSignatureApps = applications.filter(
      app => app.partner.status === 'pre_approved' && 
      app.partner.contract_sent_at && 
      !app.partner.contract_signed_at
    );
    if (needsSignatureApps.length > 0) return needsSignatureApps[0];

    // 6. Check for pre-approved with signed contract but no account
    const needsAccountApps = applications.filter(
      app => app.partner.status === 'pre_approved' && 
      app.partner.contract_signed_at && 
      !app.partner.credentials_sent_at
    );
    if (needsAccountApps.length > 0) return needsAccountApps[0];

    // 7. Check for pre-approved with account but not fully approved
    const needsFinalApprovalApps = applications.filter(
      app => app.partner.status === 'pre_approved' && 
      app.partner.credentials_sent_at
    );
    if (needsFinalApprovalApps.length > 0) return needsFinalApprovalApps[0];

    // If no unchanged applications, return first application
    return applications.length > 0 ? applications[0] : null;
  };

  const handleSelectApplication = (application: PartnerApplication | null) => {
    // Add null safety check
    if (!application || !application.partner) {
      console.warn('Attempted to select invalid application');
      return;
    }
    
    setSelectedApplication(application);
    
    // Determine workflow stages based on partner status and completion dates
    const status = application.partner.status;
    const partner = application.partner;
    
    // UPDATED: Only 2 stages in workflow - Pre-Approval Review and Account Creation
    const stages: WorkflowStage[] = [
      { 
        id: '2', 
        name: 'Pre-Approval Review', 
        status: status === 'pre_approved' || status === 'active' ? 'completed' : 
                status === 'pending' ? 'in-progress' : 'pending',
        completedAt: status === 'pre_approved' || status === 'active' ? partner.updated_at : undefined
      },
      { 
        id: '5', 
        name: 'Account Creation', 
        status: status === 'active' ? 'completed' : 
                partner.contract_signed_at && status !== 'active' ? 'in-progress' : 'pending',
        completedAt: partner.approved_date || undefined
      }
    ];
    
    setWorkflowStages(stages);
  };

  const handleAddContractConditions = async (partnerId: string, notes?: string) => {
    try {
      setWorkflowStages(prev => prev.map(stage => 
        stage.id === '4a' ? { ...stage, status: 'in-progress' } : stage
      ));

      // Validate required fields
      if (!monthlyPrice || parseFloat(monthlyPrice) <= 0) {
        alert('Please enter a valid monthly price');
        return;
      }

      const result = await partnerApplicationService.addContractConditions(
        partnerId, 
        notes,
        parseFloat(monthlyPrice),
        contractTimeframe,
        paymentTerms
      );

      if (result.success) {
        alert(`Contract conditions added successfully!\n\nPartner: ${selectedApplication?.partner.business_name}\n\nContract conditions have been documented with pricing details.`);
        
        // Reset form
        setContractConditionsNotes('');
        setMonthlyPrice('');
        setContractTimeframe(12);
        setPaymentTerms('monthly');
        
        // CRITICAL FIX: Reload and auto-refresh immediately
        await fetchApplications();
      } else {
        alert(`Failed to add contract conditions: ${result.message}`);
      }
    } catch (error) {
      console.error('Error adding contract conditions:', error);
      alert('Failed to add contract conditions. Please try again.');
    }
  };

  const handleSendContract = async (partnerId: string) => {
    try {
      if (!user) {
        alert('You must be logged in to send contracts');
        return;
      }

      // Validate PDF file is selected
      if (!contractPdfFile) {
        alert('Please upload a contract PDF before sending');
        return;
      }

      setIsUploadingPdf(true);
      setWorkflowStages(prev => prev.map(stage => 
        stage.id === '4b' ? { ...stage, status: 'in-progress' } : stage
      ));

      // Upload PDF to Supabase Storage
      const uploadResult = await partnerApplicationService.uploadContractPdf(
        partnerId,
        selectedApplication!.partner.partner_code,
        contractPdfFile
      );

      if (!uploadResult.success) {
        alert(`Failed to upload contract PDF: ${uploadResult.error}`);
        setIsUploadingPdf(false);
        return;
      }

      // Send contract with PDF URL
      const result = await partnerApplicationService.sendContractToPartner(
        partnerId, 
        user.id,
        uploadResult.url
      );

      if (result.success) {
        alert(`Contract sent successfully!\n\nPartner: ${selectedApplication?.partner.business_name}\n\nThe contract PDF has been uploaded and sent to the partner.`);
        
        // Reset form
        setContractPdfFile(null);
        
        // CRITICAL FIX: Reload and auto-refresh immediately
        await fetchApplications();
      } else {
        alert(`Failed to send contract: ${result.message}`);
      }
    } catch (error) {
      console.error('Error sending contract:', error);
      alert('Failed to send contract. Please try again.');
    } finally {
      setIsUploadingPdf(false);
    }
  };

  const handleRecordContractSignature = async (partnerId: string) => {
    try {
      setWorkflowStages(prev => prev.map(stage => 
        stage.id === '4c' ? { ...stage, status: 'in-progress' } : stage
      ));

      const result = await partnerApplicationService.recordContractSignature(partnerId);

      if (result.success) {
        alert(`Contract signature recorded successfully!\n\nPartner: ${selectedApplication?.partner.business_name}\n\nThe signed contract has been received and the partner is moving to the Account Creation stage.`);
        
        // Update workflow stages to reflect contract completion and Account Creation stage activation
        setWorkflowStages(prev => prev.map(stage => {
          if (stage.id === '4c') {
            return { ...stage, status: 'completed', completedAt: new Date().toISOString() };
          }
          if (stage.id === '5') {
            return { ...stage, status: 'in-progress' };
          }
          return stage;
        }));
        
        // CRITICAL FIX: Reload and auto-refresh immediately
        await fetchApplications();
      } else {
        alert(`Failed to record contract signature: ${result.message}`);
      }
    } catch (error) {
      console.error('Error recording contract signature:', error);
      alert('Failed to record contract signature. Please try again.');
    }
  };

  const handlePreApprove = async (partnerId: string) => {
    try {
      if (!user) {
        alert('You must be logged in to pre-approve applications');
        return;
      }

      setWorkflowStages(prev => prev.map(stage => 
        stage.id === '1' ? { ...stage, status: 'completed', completedAt: new Date().toISOString() } :
        stage.id === '2' ? { ...stage, status: 'in-progress' } :
        stage
      ));

      await partnerApplicationService.preApprovePartner(partnerId);

      const partnerName = selectedApplication?.partner.business_name;

      alert(`Partner pre-approved successfully!\n\nPartner: ${partnerName}\nStatus: Pre-Approved\n\nThe partner application is now in pre-approved status and ready for the next workflow stage.`);

      // CRITICAL FIX: Reload and auto-refresh immediately
      await fetchApplications();
    } catch (error) {
      console.error('Error pre-approving partner:', error);
      alert('Failed to pre-approve partner application. Please try again.');
    }
  };

  const handleApprove = async (partnerId: string, commissionRate: number) => {
    try {
      if (!user) {
        alert('You must be logged in to approve applications');
        return;
      }

      setWorkflowStages(prev => prev.map(stage => 
        stage.id === '4' ? { ...stage, status: 'completed', completedAt: new Date().toISOString() } :
        stage.id === '5' ? { ...stage, status: 'in-progress' } :
        stage
      ));

      await partnerApplicationService.approvePartner(partnerId, user.id, commissionRate);

      const partnerEmail = selectedApplication?.partner.contact_email;
      if (partnerEmail) {
        const tempPassword = `Partner${Math.random().toString(36).slice(-8)}!`;
        await partnerApplicationService.createPartnerAccount(partnerId, partnerEmail, tempPassword);
        
        alert(`Partner approved successfully! Temporary password: ${tempPassword}\n\nThe partner can now log in to the partner dashboard.`);
      }

      window.location.reload();
    } catch (error) {
      console.error('Error approving partner:', error);
      alert('Failed to approve partner application. Please try again.');
    }
  };

  const handleReject = async (partnerId: string, reason: string) => {
    try {
      setWorkflowStages(prev => prev.map(stage => 
        stage.id === '2' ? { ...stage, status: 'rejected' } : stage
      ));

      await partnerApplicationService.rejectPartner(partnerId, reason);

      alert('Partner application rejected. The partner will be notified via email.');

      window.location.reload();
    } catch (error) {
      console.error('Error rejecting partner:', error);
      alert('Failed to reject partner application. Please try again.');
    }
  };

  const handleDeleteApplication = async (partnerId: string) => {
    // Add confirmation dialog
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    setIsLoading(true);
    console.log('Attempting to delete partner:', partnerId);

    try {
      // Call the correct service method with partner ID
      const result = await partnerApplicationService.deleteApplication(partnerId);

      if (result.success) {
        // Remove from local state
        setApplications(prev => prev.filter(app => app.partner.id !== partnerId));
        
        // Clear selected application if it was deleted
        if (selectedApplication?.partner.id === partnerId) {
          setSelectedApplication(null);
        }

        alert('Application deleted successfully');
      } else {
        console.error('Delete failed:', result.error);
        alert(`Failed to delete application: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('An unexpected error occurred while deleting the application');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
  };

  const handleCompleteOnboardingCall = async () => {
    if (!selectedApplication) return;

    setIsCompletingCall(true);
    try {
      const result = await partnerApplicationService.completeOnboardingCall(
        selectedApplication.partner.id,
        onboardingCallNotes || undefined
      );
      
      if (result?.success) {
        alert('Onboarding call completed successfully!');
        setOnboardingCallNotes('');
        
        // CRITICAL FIX: Reload and auto-refresh immediately
        await fetchApplications();
      } else {
        alert(`Failed to complete onboarding call: ${result?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Complete onboarding call error:', error);
      alert('Failed to complete onboarding call. Please try again.');
    } finally {
      setIsCompletingCall(false);
    }
  };

  const handleOpenAccountCreation = () => {
    if (!selectedApplication) return;
    
    setAccountEmail(selectedApplication.partner.contact_email);
    setAccountFullName(selectedApplication.partner.contact_name || selectedApplication.partner.business_name);
    setAccountPhone(selectedApplication.partner.contact_phone || '');
    setShowAccountCreationModal(true);
  };

  const handleCreateAccount = async () => {
    if (!selectedApplication || !accountEmail) return;

    setIsCreatingAccount(true);
    
    try {
      // 🔥 CRITICAL CHANGE: Always use "algernon99" as initial password - user never sees this
      // Account is created with password "algernon99" and user immediately receives reset email
      const result = await partnerApplicationService.createPartnerAccountWithAuth(
        selectedApplication.partner.id,
        accountEmail,
        accountFullName,
        accountPhone,
        'algernon99' // Fixed initial password (never revealed to partner)
      );
      
      if (result?.success) {
        alert('✅ Password reset email sent! Partner can now set their own password.');
        setShowAccountCreationModal(false);
        
        await fetchApplications();
      } else {
        alert(`Failed to create account: ${result?.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Account creation error:', error);
      
      const errorMessage = error.message || 'Please try again.';
      alert(`Failed to create account: ${errorMessage}`);
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    alert('Password copied to clipboard');
  };

  const handleApproveClick = () => {
    setShowApprovalModal(true);
  };

  const confirmApproval = async () => {
    try {
      if (!selectedApplication) return;

      await partnerApplicationService.approvePartner(selectedApplication.partner.id, user!.id, commissionRate);
      
      alert('Partner approved successfully - onboarding complete!');
      setShowApprovalModal(false);
      
      // CRITICAL FIX: Reload and auto-refresh immediately
      await fetchApplications();
    } catch (error: any) {
      alert(error.message || 'Error approving partner');
    }
  };

  // ENHANCED: Auto-select next application based on workflow stage with improved logic
  const selectNextApplication = (currentStageFilter: (app: PartnerApplication) => boolean, nextStageFilter?: (app: PartnerApplication) => boolean) => {
    // Filter applications for current stage (excluding the one just completed)
    const currentStageApps = applications.filter(app => 
      app.partner.id !== selectedApplication?.partner.id && currentStageFilter(app)
    );

    if (currentStageApps.length > 0) {
      // Select oldest application in current stage
      handleSelectApplication(currentStageApps[0]);
      return;
    }

    // If no applications in current stage, try next stage
    if (nextStageFilter) {
      const nextStageApps = applications.filter(app => 
        app.partner.id !== selectedApplication?.partner.id && nextStageFilter(app)
      );
      if (nextStageApps.length > 0) {
        handleSelectApplication(nextStageApps[0]);
        return;
      }
    }

    // If no applications found in current or next stage, find oldest unchanged across all stages
    const oldestUnchanged = findOldestUnchangedApplication();
    if (oldestUnchanged) {
      handleSelectApplication(oldestUnchanged);
    } else {
      // No applications available at all
      setSelectedApplication(null);
    }
  };

  // Determine which action buttons to show based on current stage
  const showPreApprovalActions = selectedApplication?.partner.status === 'pending';
  const showOnboardingCallAction = 
    selectedApplication?.partner.status === 'pre_approved' && 
    !selectedApplication?.partner.onboarding_call_completed;
  const showContractConditionsAction = 
    selectedApplication?.partner.status === 'pre_approved' && 
    selectedApplication?.partner.onboarding_call_completed &&
    !selectedApplication?.partner.contract_conditions_added;
  const showSendContractAction = 
    selectedApplication?.partner.status === 'pre_approved' && 
    selectedApplication?.partner.contract_conditions_added &&
    !selectedApplication?.partner.contract_sent_at;
  const showRecordSignatureAction = 
    selectedApplication?.partner.status === 'pre_approved' && 
    selectedApplication?.partner.contract_sent_at &&
    !selectedApplication?.partner.contract_signed_at;
  const showCreateAccountAction = 
    selectedApplication?.partner.status === 'pre_approved' && 
    selectedApplication?.partner.contract_signed_at &&
    !selectedApplication?.partner.credentials_sent_at;
  const showFinalApprovalActions = 
    selectedApplication?.partner.status === 'pre_approved' && 
    selectedApplication?.partner.credentials_sent_at;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header with Back Button */}
      <div className="max-w-[1600px] mx-auto mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/partner-management-dashboard')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Partner Management</span>
            </button>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Onboarding Workflow</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh applications"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="font-medium">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto space-y-3">
        {/* Stage Overview Cards */}
        <div className="grid grid-cols-5 gap-3">
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900">Applications</h3>
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
          
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900">Pre-Approved</h3>
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              </div>
              <p className="text-2xl font-bold text-blue-600">{applications.filter(app => app.partner.status === 'pre_approved').length}</p>
              <p className="text-xs text-gray-500">Ready for onboarding</p>
            </div>
          </div>
          
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900">Onboarding</h3>
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              </div>
              <p className="text-2xl font-bold text-yellow-600">{applications.filter(app => app.partner.status === 'pre_approved' && !app.partner.onboarding_call_completed).length}</p>
              <p className="text-xs text-gray-500">In progress</p>
            </div>
          </div>
          
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900">Contracted</h3>
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              </div>
              <p className="text-2xl font-bold text-purple-600">{applications.filter(app => app.partner.status === 'pre_approved' && app.partner.onboarding_call_completed && !app.partner.contract_conditions_added).length}</p>
              <p className="text-xs text-gray-500">Conditions pending</p>
            </div>
          </div>
          
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-900">Approved</h3>
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-2xl font-bold text-green-600">{applications.filter(app => app.partner.status === 'active').length}</p>
              <p className="text-xs text-gray-500">Active partners</p>
            </div>
          </div>
        </div>

        {/* Main Content Grid - Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Left: Application Queue (50% width) */}
          <div className="lg:col-span-1">
            <ApplicationQueueList
              applications={applications}
              selectedApplicationId={selectedApplication?.partner.id || null}
              onSelectApplication={handleSelectApplication}
              onDeleteApplication={handleDeleteApplication}
              loading={loading}
            />
          </div>

          {/* Right: Application Review (50% width) */}
          <div className="lg:col-span-1">
            <ApplicationReviewPanel
              application={selectedApplication}
              onPreApprove={handlePreApprove}
              onApprove={handleApprove}
              onReject={handleReject}
              onAddContractConditions={handleAddContractConditions}
              onSendContract={handleSendContract}
              onRecordContractSignature={handleRecordContractSignature}
            />
          </div>
        </div>

        {/* Workflow Actions Section */}
        {selectedApplication && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Workflow Actions</h3>
            
            {/* Pre-Approval Actions */}
            {showPreApprovalActions && (
              <div className="flex gap-3">
                <button
                  onClick={() => handlePreApprove(selectedApplication.partner.id)}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Pre-Approve Partner
                </button>
                <button
                  onClick={() => handleReject(selectedApplication.partner.id, '')}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Reject Application
                </button>
              </div>
            )}

            {/* Onboarding Call Completion Action */}
            {showOnboardingCallAction && (
              <div className="space-y-3">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    Status: Pre-Approved - Onboarding Call In Progress
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Partner is ready for the onboarding call discussion
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Onboarding Call Notes (Optional)
                  </label>
                  <textarea
                    value={onboardingCallNotes}
                    onChange={(e) => setOnboardingCallNotes(e.target.value)}
                    placeholder="Enter notes from the onboarding call discussion..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                  />
                </div>
                <button
                  onClick={handleCompleteOnboardingCall}
                  disabled={isCompletingCall}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {isCompletingCall ? 'Completing...' : 'Call Done'}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  Marks the onboarding call as complete. Contract conditions will be added in the next step.
                </p>
              </div>
            )}

            {/* Contract Conditions Action (Substage 1) */}
            {showContractConditionsAction && (
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center justify-center w-6 h-6 bg-purple-600 text-white rounded-full text-xs font-bold">
                      1
                    </div>
                    <p className="text-sm font-semibold text-purple-900">
                      Contract Stage: Step 1 of 3
                    </p>
                  </div>
                  <p className="text-xs text-purple-700">
                    Add contract conditions with pricing and payment details
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Price (EUR) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={monthlyPrice}
                    onChange={(e) => setMonthlyPrice(e.target.value)}
                    placeholder="e.g., 299.99"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract Timeframe *
                  </label>
                  <div className="grid grid-cols-4 gap-3">
                    {([3, 6, 9, 12] as const).map((months) => (
                      <button
                        key={months}
                        type="button"
                        onClick={() => setContractTimeframe(months)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          contractTimeframe === months
                            ? 'border-purple-600 bg-purple-50 text-purple-900 font-semibold' :'border-gray-300 bg-white text-gray-700 hover:border-purple-400'
                        }`}
                      >
                        {months} months
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Terms *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['upfront', 'monthly'] as const).map((term) => (
                      <button
                        key={term}
                        type="button"
                        onClick={() => setPaymentTerms(term)}
                        className={`px-4 py-3 rounded-lg border-2 transition-all ${
                          paymentTerms === term
                            ? 'border-purple-600 bg-purple-50 text-purple-900 font-semibold' :'border-gray-300 bg-white text-gray-700 hover:border-purple-400'
                        }`}
                      >
                        {term.charAt(0).toUpperCase() + term.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={contractConditionsNotes}
                    onChange={(e) => setContractConditionsNotes(e.target.value)}
                    placeholder="Document any special contract conditions or requirements..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    rows={3}
                  />
                </div>

                <button
                  onClick={() => {
                    handleAddContractConditions(
                      selectedApplication.partner.id, 
                      contractConditionsNotes || undefined
                    );
                  }}
                  disabled={!monthlyPrice || parseFloat(monthlyPrice) <= 0}
                  className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Complete Step 1 - Add Conditions
                </button>
              </div>
            )}

            {/* Send Contract Action (Substage 2) */}
            {showSendContractAction && (
              <div className="space-y-3">
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center justify-center w-6 h-6 bg-indigo-600 text-white rounded-full text-xs font-bold">
                      2
                    </div>
                    <p className="text-sm font-semibold text-indigo-900">
                      Contract Stage: Step 2 of 3
                    </p>
                  </div>
                  <p className="text-xs text-indigo-700">
                    Step 1 completed ✓ - Upload and send contract PDF to partner
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="text-xs font-medium text-gray-700">Contract Details:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Monthly Price:</span>
                      <span className="ml-2 font-semibold">€{selectedApplication?.partner.monthly_price?.toFixed(2) || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Timeframe:</span>
                      <span className="ml-2 font-semibold">{selectedApplication?.partner.contract_timeframe || 'N/A'} months</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Payment Terms:</span>
                      <span className="ml-2 font-semibold capitalize">{selectedApplication?.partner.payment_terms || 'N/A'}</span>
                    </div>
                  </div>
                  {selectedApplication?.partner.contract_conditions_notes && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-700">Notes:</p>
                      <p className="text-sm text-gray-600">{selectedApplication.partner.contract_conditions_notes}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Contract PDF *
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors">
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.type !== 'application/pdf') {
                              alert('Only PDF files are allowed');
                              return;
                            }
                            if (file.size > 10 * 1024 * 1024) {
                              alert('File size must be less than 10MB');
                              return;
                            }
                            setContractPdfFile(file);
                          }
                        }}
                        className="hidden"
                      />
                      <Upload className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">
                        {contractPdfFile ? contractPdfFile.name : 'Choose PDF file...'}
                      </span>
                    </label>
                    {contractPdfFile && (
                      <button
                        onClick={() => setContractPdfFile(null)}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Max file size: 10MB. Only PDF format allowed.
                  </p>
                </div>

                <button
                  onClick={() => handleSendContract(selectedApplication.partner.id)}
                  disabled={!contractPdfFile || isUploadingPdf}
                  className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  {isUploadingPdf ? 'Uploading...' : 'Complete Step 2 - Send Contract'}
                </button>
              </div>
            )}

            {/* Record Signature Action (Substage 3) */}
            {showRecordSignatureAction && (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center justify-center w-6 h-6 bg-green-600 text-white rounded-full text-xs font-bold">
                      3
                    </div>
                    <p className="text-sm font-semibold text-green-900">
                      Contract Stage: Step 3 of 3
                    </p>
                  </div>
                  <p className="text-xs text-green-700">
                    Steps 1-2 completed ✓ - Final step: Record received contract
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    Contract Sent: {new Date(selectedApplication.partner.contract_sent_at!).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => handleRecordContractSignature(selectedApplication.partner.id)}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Complete Step 3 - Record Received Contract
                </button>
              </div>
            )}

            {/* Create Account Action - After contract is signed */}
            {showCreateAccountAction && (
              <div className="space-y-3">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">
                    Contract Signed: {new Date(selectedApplication.partner.contract_signed_at!).toLocaleString()}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Ready to create partner account with login credentials
                  </p>
                </div>
                <button
                  onClick={handleOpenAccountCreation}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Account
                </button>
              </div>
            )}

            {/* Final Approval Actions - After account is created */}
            {showFinalApprovalActions && (
              <div className="space-y-3">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">
                    Account Created: {new Date(selectedApplication.partner.credentials_sent_at!).toLocaleString()}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Partner can now log in to the partner dashboard
                  </p>
                </div>
                <button
                  onClick={handleApproveClick}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center font-medium"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Complete Onboarding
                </button>
              </div>
            )}
          </div>
        )}

        {/* Bottom: Workflow Progress (Full width, horizontal) */}
        <div className="w-full">
          <WorkflowProgressTracker stages={workflowStages} />
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Approve Partner Application</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission Rate (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Approving this application will create a partner account and grant access to the partner dashboard.
                </p>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApproval}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Confirm Approval
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Creation Modal */}
      {showAccountCreationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <UserPlus className="w-6 h-6 mr-2 text-blue-600" />
                Create Partner Account
              </h3>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 mb-2">Partner Information</p>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p><strong>Business:</strong> {selectedApplication?.partner.business_name}</p>
                    <p><strong>Code:</strong> {selectedApplication?.partner.partner_code}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="partner@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={accountFullName}
                    onChange={(e) => setAccountFullName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={accountPhone}
                    onChange={(e) => setAccountPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1234567890"
                  />
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-green-900 mb-2">✅ Password Reset Email Process</p>
                      <ul className="text-sm text-green-800 space-y-1.5">
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">1.</span>
                          <span>System creates account with secure initial password (never shown to partner)</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">2.</span>
                          <span><strong>Password reset email sent automatically</strong> to {accountEmail || 'partner email'}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">3.</span>
                          <span>Partner clicks reset link and creates their own password</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 font-bold">4.</span>
                          <span>Partner can then login with their new password</span>
                        </li>
                      </ul>
                      <p className="text-xs text-green-700 mt-3 italic">
                        Note: Partner never sees the initial password - they set their own from the start
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowAccountCreationModal(false);
                    }}
                    disabled={isCreatingAccount}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAccount}
                    disabled={!accountEmail || !accountFullName || isCreatingAccount}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-md"
                  >
                    {isCreatingAccount ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending Password Reset...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" clipRule="evenodd" />
                        </svg>
                        Send Password Reset Email
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerOnboardingWorkflow;